import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Calendar, Package, Truck, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Helmet } from "react-helmet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { language, t } = useLanguage();
  const [homepageContent, setHomepageContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHomepageContent = async () => {
      try {
        const res = await axios.get(`${API}/settings/homepage`);
        setHomepageContent(res.data);
      } catch (e) {
        console.error("Failed to fetch homepage content:", e);
        // Use fallback content
        setHomepageContent({
          hero_image_url: "https://images.unsplash.com/photo-1769812343322-f4a6e73c8aa7?crop=entropy&cs=srgb&fm=jpg&w=800",
          equipment_categories: []
        });
      } finally {
        setLoading(false);
      }
    };
    fetchHomepageContent();
  }, []);

  const featureIcons = [Calendar, Package, Truck, CheckCircle];

  // Get categories from dynamic content or use fallback
  const categories = homepageContent?.equipment_categories || [];

  // JSON-LD Local Business Schema (hidden SEO)
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Magnolia Events and Rentals LLC",
    "description": language === "es" 
      ? "Renta de sillas, mesas y brinca brincas para eventos en Palm Beach County"
      : "Affordable party rentals including chairs, tables, and bouncy castles for events in Palm Beach County",
    "url": "https://magnoliaeventsandrentals.com",
    "telephone": "(561) 501-1241",
    "email": "info@magnoliaeventsandrentals.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6268 Floridian Cir",
      "addressLocality": "Lake Worth",
      "addressRegion": "FL",
      "postalCode": "33463",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "26.6178",
      "longitude": "-80.0942"
    },
    "areaServed": [
      "Lake Worth", "West Palm Beach", "Boca Raton", "Boynton Beach", 
      "Delray Beach", "Wellington", "Jupiter", "Palm Beach Gardens",
      "Greenacres", "Royal Palm Beach", "Riviera Beach", "Lantana", "Palm Springs"
    ],
    "priceRange": "$",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "08:00",
      "closes": "20:00"
    },
    "sameAs": [
      "https://www.instagram.com/magnolia.eventsrental",
      "https://www.facebook.com/share/1AcmPpFtMc/"
    ]
  };

  return (
    <div className="relative">
      {/* SEO Meta Tags & JSON-LD Schema - Keywords in meta, not visible headers */}
      <Helmet>
        <title>
          {language === "es" 
            ? "Magnolia Events | Renta de Sillas y Mesas para Fiestas en Palm Beach County"
            : "Magnolia Events | Affordable Party Rentals Palm Beach County - Chairs, Tables, Bouncy Castles"}
        </title>
        <meta name="description" content={
          language === "es"
            ? "Renta de sillas y mesas para fiestas, renta de brinca brincas en Lake Worth y West Palm Beach. Alquiler de equipo para eventos en Palm Beach County. Servicio confiable y precios accesibles."
            : "Affordable party rentals Palm Beach County. Premium chair and table rentals Lake Worth. Bouncy castle & inflatable rentals West Palm Beach. Event equipment hire for weddings and parties South Florida."
        } />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>

      {/* Hero Section - Restored Original Brand Headers */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block bg-sage/10 text-sage font-medium px-4 py-2 rounded-full text-sm mb-6">
                {t.hero.badge}
              </span>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight mb-6">
                {homepageContent?.hero_title_en || homepageContent?.hero_title_es ? (
                  // Use dynamic content from admin
                  language === "es" 
                    ? (homepageContent?.hero_title_es || homepageContent?.hero_title_en)
                    : (homepageContent?.hero_title_en || t.hero.title + " " + t.hero.titleHighlight)
                ) : (
                  // Fallback to translations with highlight
                  <>{t.hero.title}{" "}<span className="text-sage">{t.hero.titleHighlight}</span></>
                )}
              </h1>
              <p className="text-lg text-charcoal/70 mb-8 max-w-xl">
                {language === "es"
                  ? (homepageContent?.hero_description_es || t.hero.subtitle)
                  : (homepageContent?.hero_description_en || t.hero.subtitle)
                }
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/products">
                  <Button
                    data-testid="hero-browse-btn"
                    className="bg-sage hover:bg-sage-dark text-white rounded-full px-8 py-6 text-lg font-bold btn-hover"
                  >
                    {t.hero.browseBtn}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="hero-contact-btn"
                    variant="outline"
                    className="border-2 border-charcoal/20 text-charcoal hover:bg-charcoal/5 rounded-full px-8 py-6 text-lg font-bold"
                  >
                    {t.hero.contactBtn}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={homepageContent?.hero_image_url || "https://images.unsplash.com/photo-1769812343322-f4a6e73c8aa7?crop=entropy&cs=srgb&fm=jpg&w=800"}
                  alt={language === "es" 
                    ? "Configuración elegante de evento | Elegant event setup" 
                    : "Elegant event setup | Configuración elegante de evento"
                  }
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-charcoal/60">{t.hero.startingFrom}</p>
                      <p className="font-heading font-bold text-2xl text-charcoal">$5/day</p>
                    </div>
                    <div className="bg-sage/20 text-sage font-bold px-4 py-2 rounded-full text-sm">
                      {t.hero.bestValue}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Restored Original H2 */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-charcoal mb-4">
              {t.features.title}
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.features.items.map((feature, index) => {
              const Icon = featureIcons[index] || CheckCircle;
              return (
                <div
                  key={index}
                  className={`bg-white/70 border border-sage/10 p-8 rounded-3xl hover:bg-white hover:shadow-xl transition-all duration-300 animate-fade-in stagger-${index + 1}`}
                  data-testid={`feature-${index}`}
                >
                  <div className="w-14 h-14 bg-sage/10 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-sage" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-charcoal mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal/60">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Section - Restored Original H2 */}
      <section className="py-20 lg:py-32 bg-sage">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 gap-4">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-2">
                {t.equipment.title}
              </h2>
              <p className="text-white/80">{t.equipment.subtitle}</p>
            </div>
            <Link to="/products">
              <Button
                data-testid="view-all-products-btn"
                variant="outline"
                className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full"
              >
                {t.equipment.viewAll}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {categories.length === 0 ? (
              <div className="col-span-3 text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-white/50" />
              </div>
            ) : categories.map((category, index) => {
              // Use dynamic content from database
              const titleEn = category.title_en || category.key;
              const titleEs = category.title_es || titleEn;
              const descEn = category.description_en || "";
              const descEs = category.description_es || descEn;
              const altText = language === "es" 
                ? `${titleEs} | ${titleEn}`
                : `${titleEn} | ${titleEs}`;
              
              return (
                <Link
                  key={category.id || category.key}
                  to={category.link || "/products"}
                  data-testid={`category-${category.key}`}
                  className={`group relative overflow-hidden bg-white rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 card-hover animate-fade-in stagger-${index + 1}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={category.image_url}
                      alt={altText}
                      className="w-full h-full object-cover img-zoom"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-charcoal mb-1">
                      {language === "es" ? titleEs : titleEn}
                    </h3>
                    <p className="text-charcoal/60">{language === "es" ? descEs : descEn}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-charcoal mb-4">
              {t.howItWorks.title}
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              {t.howItWorks.subtitle}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.howItWorks.steps.map((item, index) => (
              <div
                key={index}
                className={`relative animate-fade-in stagger-${index + 1}`}
                data-testid={`step-${item.step}`}
              >
                <div className="text-6xl font-heading font-bold text-sage/10 mb-4">
                  0{item.step}
                </div>
                <h3 className="font-heading font-bold text-xl text-charcoal mb-2">
                  {item.title}
                </h3>
                <p className="text-charcoal/60">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/booking">
              <Button
                data-testid="start-booking-btn"
                className="bg-sage hover:bg-sage-dark text-white rounded-full px-8 py-6 text-lg font-bold btn-hover"
              >
                {t.cta.button}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-charcoal relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              {t.cta.title}
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
              {t.cta.subtitle}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/products">
                <Button
                  data-testid="cta-browse-btn"
                  className="bg-sage hover:bg-sage-light text-white rounded-full px-8 py-6 text-lg font-bold btn-hover"
                >
                  {t.hero.browseBtn}
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="cta-contact-btn"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-bold"
                >
                  {t.hero.contactBtn}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
